/**
 * Motivo de un rechazo de Binary, a partir del texto que guardó el server.
 *
 * prospectos (el servicio que carga en Binary) responde
 * "No se pudo procesar la solicitud N. Motivo: X". Los rechazos anteriores a ese
 * cambio quedaron guardados sin el motivo ("No se pudo procesar la solicitud N."):
 * para esos se dice que Binary no lo informó y que va a aparecer en el próximo
 * intento, en vez de repetir una frase que no dice qué corregir.
 */
export interface RechazoBinary {
  /** Qué pasó, en una frase para la persona que tiene que corregirlo. */
  motivo: string;
  /** Número de solicitud en Binary, para buscarla si hace falta. */
  solicitud: string | null;
  /** true si el rechazo quedó guardado sin motivo (antes del cambio en prospectos). */
  sinMotivo: boolean;
}

const CON_SOLICITUD = /^No se pudo procesar la solicitud (\d+)\.?\s*(?:Motivo:\s*([\s\S]+))?$/i;
const ERROR_EN_SOLICITUD = /^Error en solicitud (\d+)\s*-\s*Error:\s*([\s\S]+)$/i;

/** "ORA-20010: AFI812PRC: El afiliado..." → "El afiliado..." (formato viejo, crudo). */
function sinCodigosOracle(texto: string): string {
  const limpio = texto
    .split('\n')[0]
    .replace(/ORA-\d{5}:\s*/g, '')
    .replace(/^\s*(?=[A-Z0-9_$#.]*(?:\d|_|PRC|PCK|PKG))[A-Z][A-Z0-9_$#.]{3,}\s*:\s*/, '')
    .replace(/[\s:]+$/, '')
    .trim();
  return limpio ? `${limpio.replace(/\.$/, '')}.` : texto.trim();
}

function motivoConocido(texto: string): string | null {
  if (/ThrottlerException|Too Many Requests|HTTP 429/i.test(texto)) {
    return 'Binary estaba saturado por demasiados envíos seguidos. Se reintenta solo.';
  }
  const http = /prospectos respondió HTTP (\d{3})/i.exec(texto);
  if (http) return `El servicio que carga en Binary no respondió (error ${http[1]}).`;
  if (/timeout|timed out|ETIMEDOUT|ECONNRESET|ECONNREFUSED/i.test(texto)) {
    return 'No se pudo conectar con el servicio que carga en Binary.';
  }
  return null;
}

export function leerRechazoBinary(mensaje?: string | null): RechazoBinary | null {
  const texto = (mensaje ?? '').trim();
  if (!texto) return null;

  const conSolicitud = CON_SOLICITUD.exec(texto);
  if (conSolicitud) {
    const motivo = conSolicitud[2]?.trim();
    return motivo
      ? { motivo, solicitud: conSolicitud[1], sinMotivo: false }
      : { motivo: 'Binary no informó el motivo.', solicitud: conSolicitud[1], sinMotivo: true };
  }
  const errorEn = ERROR_EN_SOLICITUD.exec(texto);
  if (errorEn) return { motivo: sinCodigosOracle(errorEn[2]), solicitud: errorEn[1], sinMotivo: false };
  if (/ORA-\d{5}:/.test(texto)) return { motivo: sinCodigosOracle(texto), solicitud: null, sinMotivo: false };

  return { motivo: motivoConocido(texto) ?? texto, solicitud: null, sinMotivo: false };
}
